import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import ArgonBox from 'component/Argon/ArgonBox';
import ArgonTypography from 'component/Argon/ArgonTypography';
import Stack from '@mui/material/Stack';

import DashboardLayout from 'layout/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'component/Navbars/DashboardNavbar';

import DataTable from 'component/Tables/DataTable';

import { RefreshIcon, DownloadIcon } from './components/ActionIcon';

import { useOrder } from 'api/admin/getOrder';

import ExportToExcel from 'component/ExportToExcel';
import ExportToZip from 'component/ExportToZip';
import ArgonBadge from 'component/Argon/ArgonBadge';
import { Tooltip } from '@mui/material';
import mongoIdToDt from 'utils/mongoIdToDt';
import { usePolicyDownload } from 'api/postPolicyDownload';

function Order() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isSuccess, isError } = useOrder(invoiceId);
  const { mutateAsync: downloadPolicy } = usePolicyDownload();

  const [disableExp, setDisableExp] = useState(true);

  useEffect(() => {
    if (isError) {
      navigate('/notfound');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSuccess, isError, data]);

  useEffect(() => {
    if (data?.data) {
      const value = data.data.reduce((prv, cur) => {
        if (cur.urlPolicyLocal) {
          return prv + 1;
        }
        return prv + 0;
      }, 0);
      if (value === data.data.length && value !== 0) {
        setDisableExp(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  if (isLoading) {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <DashboardLayout bgColor="success">
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <ArgonBox>
              <Card>
                <ArgonBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
                  <ArgonBox lineHeight={1}>
                    <ArgonTypography variant="h5" fontWeight="medium">
                      รายการสั่งซื้อ {invoiceId}
                    </ArgonTypography>
                    <ArgonTypography variant="button" fontWeight="regular" color="text">
                      วันที่ {mongoIdToDt(data._id)}
                    </ArgonTypography>
                  </ArgonBox>
                  <Stack spacing={1} direction="row">
                    <ExportToExcel data={data.data} fileName={invoiceId} disabled={disableExp} />
                  </Stack>
                </ArgonBox>
                <ArgonBox display="flex" justifyContent="end" px={3}>
                  <Stack spacing={1} direction="row">
                    <ArgonTypography fontWeight="medium">download to zip part: </ArgonTypography>
                    {new Array(Math.ceil(data.data.length / 20)).fill('').map((x, i) => (
                      <ExportToZip key={i} invoiceId={invoiceId} disabled={disableExp} limit={20} skip={i * 20}>
                        {`${i + 1}`}
                      </ExportToZip>
                    ))}
                  </Stack>
                </ArgonBox>

                <ArgonBox px={3} mt={3}>
                  <DataTable
                    table={{
                      columns: [
                        { Header: 'ชื่อ', Cell: ({ row }) => <TdMenu row={row} downloadPolicy={downloadPolicy} /> },
                        {
                          Header: 'สถานะ',
                          minWidth: 4,
                          width: 6,
                          Cell: ({ row }) => <TdStatus row={row} />,
                        },

                        { Header: 'เลขที่กรมธรรม์', maxWidth: 10, minWidth: 10, width: 10, accessor: 'policyNo' },
                        {
                          Header: 'หมายเหตุ',
                          Cell: (props) => <TdNote {...props} />,
                        },
                      ],
                      rows: data.data,
                    }}
                    entriesPerPage={{
                      defaultValue: 25,
                      entries: [25, 50, 100],
                    }}
                    canSearch
                  />
                </ArgonBox>
              </Card>
            </ArgonBox>
          </Grid>
        </Grid>
      </ArgonBox>
    </DashboardLayout>
  );
}

export default Order;

const Badge = <ArgonBadge variant="contained" color="secondary" size="xs" badgeContent="รอดำเนินการ" container />;
const FailedBadge = <ArgonBadge variant="contained" color="error" size="xs" badgeContent="ผิดพลาด" container />;
const PendingBadge = <ArgonBadge variant="contained" color="secondary" size="xs" badgeContent="ดำเนินการ" container />;
const SuccessBadge = <ArgonBadge variant="contained" color="success" size="xs" badgeContent="สำเร็จ" container />;

function TdStatus({ row }) {
  const { status } = row.original;
  if (status === 'error') {
    return FailedBadge;
  } else if (status === 'process') {
    return PendingBadge;
  } else if (status === 'success') {
    return SuccessBadge;
  }
  return Badge;
}

function TdMenu({ row, downloadPolicy }) {
  const value = row.original;

  function downloadPDF() {
    if (value?.urlPolicyLocal) {
      let link = document.createElement('a');
      link.rel = 'noopener noreferrer';
      link.target = '_blank';
      link.href = process.env.REACT_APP_DO_SPACE + value?.urlPolicyLocal;
      link.setAttribute('download', value.policyNo);

      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
  }

  function handleResolveError() {
    if (value?.error) {
      /** @type {string} */
      const msg = value.error;
      // handle duplicate policy
      if (msg.includes('status F, code: E004, description: พบกรมธรรม์ซ้ำซ้อนช่วงเวลาเดียวกันในระบบ')) {
        const policyNo = msg.split('เลขกรมธรรม์: ')[1]?.split(' ')[0];
        if (policyNo) {
          downloadPolicy({ _id: value._id, policyNo }).catch((e) => console.log(e));
        }
      }
    }
  }

  return (
    <Stack direction="row" alignItems="center">
      {value?.status === 'create' ? (
        <DownloadIcon />
      ) : value?.status === 'process' ? (
        <DownloadIcon title="ดำเนินการ" />
      ) : value?.status === 'success' ? (
        <ArgonBox onClick={downloadPDF}>
          <DownloadIcon title="ดาวน์โหลด" color="success" />
        </ArgonBox>
      ) : (
        <ArgonBox onClick={handleResolveError}>
          <RefreshIcon />
        </ArgonBox>
      )}
      {value.name}
    </Stack>
  );
}

function TdNote({ row, column }) {
  const value = row?.original;
  if (value?.status === 'error' && value?.error) {
    const { error } = value;
    const desc = error.split('description: ');
    return (
      <Tooltip title={error}>
        <ArgonBox
          sx={{
            minWidht: column?.width || 150,
            maxWidth: 400,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {desc.length > 1 ? desc[1] : error}
        </ArgonBox>
      </Tooltip>
    );
  }
  return '';
}
